import type { DeclarationGuidance, DeclarationGuidanceType } from './component/DeclarationGuidanceDialog';
import type { DeclarationNextStep } from './nextStep';

import { i18n } from '../../../shared/language';

const guidanceTypeMap: Record<string, DeclarationGuidanceType> = {
  AUTH_DIRECT: 'AUTH_DIRECT',
  VerifyAtTaxAuthority: 'AUTH_DIRECT',
  AUTH_BIOMETRIC: 'AUTH_BIOMETRIC',
  VerifyBiometric: 'AUTH_BIOMETRIC',
  AUTH_OTP: 'AUTH_OTP',
  VerifyOtp: 'AUTH_OTP',
  EXPLANATION: 'EXPLANATION',
  ExplainRisk: 'EXPLANATION',
};

const getGuidanceType = (value: any): DeclarationGuidanceType | null => {
  const normalized = guidanceTypeMap[String(value || '')];
  return normalized || null;
};

interface GuidanceDefinition {
  type: DeclarationGuidanceType;
  titleKey: string;
  subjectKey: string | string[];
  deadlineKey: string;
  documentsKey?: string[];
  stepsKey?: string[];
  stepsTextKey?: string;
  noteKey: string;
  guideUrl?: string;
  appStoreUrl?: string;
  googlePlayUrl?: string;
}

const localGuidance: Record<DeclarationGuidanceType, GuidanceDefinition> = {
  AUTH_DIRECT: {
    type: 'AUTH_DIRECT',
    titleKey: 'declarationGuidance.authDirectTitle',
    subjectKey: 'declarationGuidance.authDirectSubject',
    deadlineKey: 'declarationGuidance.authDirectDeadline',
    documentsKey: [
      'declarationGuidance.authDirectDocumentId',
      'declarationGuidance.authDirectDocumentPortrait',
      'declarationGuidance.authDirectDocumentNotice',
    ],
    stepsTextKey: 'declarationGuidance.authDirectSteps',
    noteKey: 'declarationGuidance.commonNote',
  },
  AUTH_BIOMETRIC: {
    type: 'AUTH_BIOMETRIC',
    titleKey: 'declarationGuidance.authBiometricTitle',
    subjectKey: [
      'declarationGuidance.authBiometricSubjectFirst',
      'declarationGuidance.authBiometricSubjectSecond',
    ],
    deadlineKey: 'declarationGuidance.authBiometricDeadline',
    stepsTextKey: 'declarationGuidance.mobileSteps',
    guideUrl: 'https://hdsd.minvoice.com.vn/tai-lieu/etax-mobile/to-khai-xac-thuc/#__tabbed_1_1',
    appStoreUrl: 'https://apps.apple.com/vn/app/etax-mobile/id1589629788',
    googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.gdt.etaxmobile',
    noteKey: 'declarationGuidance.commonNote',
  },
  AUTH_OTP: {
    type: 'AUTH_OTP',
    titleKey: 'declarationGuidance.authOtpTitle',
    subjectKey: 'declarationGuidance.authOtpSubject',
    deadlineKey: 'declarationGuidance.authOtpDeadline',
    stepsTextKey: 'declarationGuidance.mobileSteps',
    guideUrl: 'https://hdsd.minvoice.com.vn/tai-lieu/etax-mobile/to-khai-xac-thuc/#__tabbed_1_2',
    appStoreUrl: 'https://apps.apple.com/vn/app/etax-mobile/id1589629788',
    googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.gdt.etaxmobile',
    noteKey: 'declarationGuidance.commonNote',
  },
  EXPLANATION: {
    type: 'EXPLANATION',
    titleKey: 'declarationGuidance.explanationTitle',
    subjectKey: [
      'declarationGuidance.explanationSubjectFirst',
      'declarationGuidance.explanationSubjectSecond',
      'declarationGuidance.explanationSubjectThird',
    ],
    deadlineKey: 'declarationGuidance.explanationDeadline',
    stepsKey: [
      'declarationGuidance.explanationStepFirst',
      'declarationGuidance.explanationStepSecond',
      'declarationGuidance.explanationStepThird',
    ],
    noteKey: 'declarationGuidance.commonNote',
  },
};

export const getDeclarationGuidance = (nextStep: DeclarationNextStep): DeclarationGuidance | null => {
  const type = getGuidanceType(nextStep.guideType || nextStep.value);
  const definition = type ? localGuidance[type] : null;
  if (!definition) return null;

  // Chỉ lưu key trong dữ liệu hướng dẫn; nội dung hiển thị được dịch theo locale hiện tại.
  const translateValue = (key: string | string[]) => Array.isArray(key)
    ? key.map((item) => i18n.t(item))
    : i18n.t(key);

  return {
    type: definition.type,
    title: i18n.t(definition.titleKey),
    subject: translateValue(definition.subjectKey),
    deadlineText: i18n.t(definition.deadlineKey),
    documents: definition.documentsKey ? translateValue(definition.documentsKey) as string[] : undefined,
    steps: definition.stepsKey ? translateValue(definition.stepsKey) as string[] : undefined,
    stepsText: definition.stepsTextKey ? i18n.t(definition.stepsTextKey) : undefined,
    note: i18n.t(definition.noteKey),
    guideUrl: definition.guideUrl,
    appStoreUrl: definition.appStoreUrl,
    googlePlayUrl: definition.googlePlayUrl,
  };
};
